import React, { useEffect, useRef, useState } from 'react'
import Button from '../_library/Button'
import {
  bufferToBase64url,
  isPasskeyRpIdValidForOrigin,
  isPasskeyUnavailableOnOrigin,
  passkeyRequest,
  prepareGetOptions,
} from './passkey'

const AUTH_FAILED = 'Could not sign in with a passkey. Try again or use your password.'
const SESSION_FAILED = 'Signed in, but your session could not be loaded. Refresh and try again.'

const hasDesktopPasskeyBridge = () =>
  !!(window.desktop && window.desktop.preparePasskeySignIn && window.desktop.completePasskeySignIn)

const isWebAuthnGetSupported = () =>
  !!(window.PublicKeyCredential && navigator.credentials && typeof navigator.credentials.get === 'function')

const isConditionalMediationAvailable = async () => {
  try {
    return !!(
      window.PublicKeyCredential &&
      window.PublicKeyCredential.isConditionalMediationAvailable &&
      (await window.PublicKeyCredential.isConditionalMediationAvailable())
    )
  } catch (e) {
    return false
  }
}

const isPlatformAuthenticatorAvailable = async () => {
  try {
    return !!(
      window.PublicKeyCredential &&
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable &&
      (await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable())
    )
  } catch (e) {
    return false
  }
}

const PasskeyLoginButton = ({ disabled = false, onSuccess, onError, onLoadingChange, className }) => {
  const [loading, setLoading] = useState(false)
  const [visible, setVisible] = useState(false)
  const abortRef = useRef(null)
  const modalInFlightRef = useRef(false)
  const conditionalInFlightRef = useRef(false)
  const onSuccessRef = useRef(onSuccess)
  const onErrorRef = useRef(onError)
  const onLoadingChangeRef = useRef(onLoadingChange)
  const disabledRef = useRef(disabled)

  onSuccessRef.current = onSuccess
  onErrorRef.current = onError
  onLoadingChangeRef.current = onLoadingChange
  disabledRef.current = disabled

  const isSupported =
    typeof window !== 'undefined' && (hasDesktopPasskeyBridge() || isWebAuthnGetSupported())

  const runCeremony = async ({ mediation } = {}) => {
    if (disabledRef.current) return

    const isConditional = mediation === 'conditional'

    if (isConditional) {
      if (modalInFlightRef.current || conditionalInFlightRef.current) return
      conditionalInFlightRef.current = true
    } else {
      if (modalInFlightRef.current) return
      if (abortRef.current) {
        abortRef.current.abort()
        abortRef.current = null
      }
      conditionalInFlightRef.current = false
      modalInFlightRef.current = true
    }

    const abortController = typeof AbortController !== 'undefined' ? new AbortController() : null
    if (isConditional && abortController) {
      abortRef.current = abortController
    }

    let desktopHandoffId = null
    if (!isConditional) {
      setLoading(true)
      if (onLoadingChangeRef.current) onLoadingChangeRef.current(true)
    }

    try {
      if (hasDesktopPasskeyBridge()) {
        const prepared = await window.desktop.preparePasskeySignIn()
        desktopHandoffId = prepared.handoff_id
        const beginData = await passkeyRequest('/passkey/desktop/auth/begin', 'post', prepared)
        const authorizeUrl = beginData.data && beginData.data.authorize_url
        if (!authorizeUrl) throw new Error(AUTH_FAILED)
        const exchange = await window.desktop.completePasskeySignIn(prepared.handoff_id, authorizeUrl)
        await passkeyRequest('/passkey/desktop/auth/exchange', 'post', exchange)
      } else {
        const beginData = await passkeyRequest('/passkey/auth/begin', 'post', {})
        const challengeToken = beginData.data && beginData.data.challenge_token
        const getArgs = beginData.data && beginData.data.get_args
        if (!challengeToken || !getArgs) {
          throw new Error(AUTH_FAILED)
        }

        const rpId = getArgs.publicKey && getArgs.publicKey.rpId
        if (typeof rpId === 'string' && rpId && !isPasskeyRpIdValidForOrigin(rpId, window.location.hostname)) {
          console.warn(
            'The relying party ID is not a registrable domain suffix of, nor equal to the current domain. (' +
              rpId +
              ' / ' +
              window.location.hostname +
              ')',
          )
          setVisible(false)
          return
        }

        const getOptions = {
          ...prepareGetOptions(getArgs),
          ...(mediation ? { mediation } : {}),
          ...(abortController ? { signal: abortController.signal } : {}),
        }

        const credential = await navigator.credentials.get(getOptions)
        if (!credential) {
          throw new Error(AUTH_FAILED)
        }
        const r = credential.response

        const payload = {
          challenge_token: challengeToken,
          credential_id: bufferToBase64url(credential.rawId),
          client_data_json: bufferToBase64url(r.clientDataJSON),
          authenticator_data: bufferToBase64url(r.authenticatorData),
          signature: bufferToBase64url(r.signature),
        }
        if (r.userHandle) {
          payload.user_handle = bufferToBase64url(r.userHandle)
        }

        await passkeyRequest('/passkey/auth/complete', 'post', payload)
      }

      if (isConditional) {
        setLoading(true)
        if (onLoadingChangeRef.current) onLoadingChangeRef.current(true)
      }

      const sessionLoaded = onSuccessRef.current ? await onSuccessRef.current() : true
      if (sessionLoaded === false) {
        throw new Error(SESSION_FAILED)
      }
    } catch (err) {
      const aborted = (err && err.name === 'AbortError') || (abortController && abortController.signal.aborted)
      if (aborted || (err && err.name === 'NotAllowedError')) {
        return
      }
      if (isPasskeyUnavailableOnOrigin(err)) {
        console.warn((err && err.message) || err)
        setVisible(false)
        return
      }
      if (onErrorRef.current) onErrorRef.current((err && err.message) || AUTH_FAILED)
    } finally {
      if (desktopHandoffId && window.desktop && window.desktop.cancelPasskeySignIn) {
        try {
          await window.desktop.cancelPasskeySignIn(desktopHandoffId)
        } catch (cancelError) {
          console.error('Could not clean up desktop passkey handoff', cancelError)
        }
      }
      if (abortRef.current === abortController) {
        abortRef.current = null
      }
      if (isConditional) {
        conditionalInFlightRef.current = false
      } else {
        modalInFlightRef.current = false
      }
      setLoading(false)
      if (onLoadingChangeRef.current) onLoadingChangeRef.current(false)
    }
  }

  useEffect(() => {
    if (disabled) {
      if (abortRef.current) abortRef.current.abort()
      abortRef.current = null
    }
  }, [disabled])

  useEffect(() => {
    if (!isSupported) return undefined

    let cancelled = false

    const originAllowsPasskeys = async () => {
      if (hasDesktopPasskeyBridge()) return true
      try {
        const beginData = await passkeyRequest('/passkey/auth/begin', 'post', {})
        const rpId = beginData && beginData.data && beginData.data.get_args && beginData.data.get_args.publicKey
          ? beginData.data.get_args.publicKey.rpId
          : null
        if (typeof rpId === 'string' && rpId) {
          const hostname = window.location.hostname
          if (!isPasskeyRpIdValidForOrigin(rpId, hostname)) {
            console.warn(
              'The relying party ID is not a registrable domain suffix of, nor equal to the current domain. (' +
                rpId +
                ' / ' +
                hostname +
                ')',
            )
            return false
          }
        }
        return true
      } catch (err) {
        if (isPasskeyUnavailableOnOrigin(err)) {
          console.warn((err && err.message) || err)
          return false
        }
        return true
      }
    }

    const autoStart = async () => {
      if (disabledRef.current) return

      if (!(await originAllowsPasskeys()) || cancelled) return
      setVisible(true)

      if (!hasDesktopPasskeyBridge() && (await isConditionalMediationAvailable())) {
        if (cancelled || disabledRef.current) return
        await runCeremony({ mediation: 'conditional' })
        return
      }

      const shouldOpenModal = hasDesktopPasskeyBridge() || (await isPlatformAuthenticatorAvailable())
      if (cancelled || disabledRef.current || !shouldOpenModal) return
      await runCeremony()
    }

    autoStart()

    return () => {
      cancelled = true
      if (abortRef.current) abortRef.current.abort()
      abortRef.current = null
    }
    // Auto-start once when WebAuthn support is known — not when `disabled` toggles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupported])

  if (!visible) return null

  return (
    <div className="text-center">
      <Button
        type="button"
        className={className || 'login_btn passkey_btn'}
        disabled={disabled}
        loading={loading}
        onClick={() => runCeremony()}
      >
        Sign in with passkey
      </Button>
    </div>
  )
}

export default PasskeyLoginButton
